import React, { useEffect, useMemo, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api, AssignmentAttrs } from "../api"
import { SearchableSelect } from "@UILibrary/SearchableSelect"
import { ClientMultiSelect, ClientOption } from "../components/ClientMultiSelect"
import { Modal, ConfirmDialog } from "@UILibrary/Modal"
import { Button } from "@components/Button"

type Strategy = AssignmentAttrs["access_strategy"]
type Paraplanner = { id: number; fullName: string; email: string }

type AssignmentRow = {
  id: string
  attributes: {
    paraplannerId: number
    wealthManagerId: number
    accessStrategy: Strategy
    clientIds: number[]
  }
}

type Person = { id: string; attributes: { fullName: string } }

// Plain-language access levels. allow_some / deny_some reveal a client picker.
const ACCESS_CHOICES: { value: Strategy; label: string }[] = [
  { value: "allow_all", label: "Can see all of this wealth manager's clients" },
  { value: "allow_some", label: "Can see only the clients I choose" },
  { value: "deny_some", label: "Can see all clients except the ones I choose" },
  { value: "allow_none", label: "Cannot see any clients" },
]

const isSubset = (s: Strategy) => s === "allow_some" || s === "deny_some"

// One-line summary of an assignment's access, shown in the left-hand list.
const accessSummary = (s: Strategy, clientCount: number): string => {
  switch (s) {
    case "allow_all":
      return "All clients"
    case "allow_none":
      return "No clients"
    case "allow_some":
      return `Only ${clientCount} chosen`
    case "deny_some":
      return `All except ${clientCount}`
  }
}

// executeRequest carries the controller's `{ error: ... }` through as the
// Error message, so append it when there is one.
const failureText = (error: unknown, prefix: string): string => {
  const reason = (error as { message?: string } | null)?.message
  return `${prefix}${reason ? `: ${reason}` : ""}. Please try again.`
}

// Why "Save clients" is unavailable, for the button's tooltip.
const saveHint = (saving: boolean, namesPending: boolean, namesFailed: boolean, dirty: boolean): string | undefined => {
  if (saving) return "Saving…"
  if (namesPending) return "Loading the clients this assignment already has…"
  if (namesFailed) return "Couldn't load the current clients — reload before saving"
  if (!dirty) return "No changes to save"
  return undefined
}

const clientFetcher = (wealthManagerId: number) => (q: string) =>
  api.clients.index({ q, wealth_manager_id: wealthManagerId, per_page: 20 })

type Props = { paraplanner: Paraplanner; onBack: () => void }

export const ParaplannerShow: React.FC<Props> = ({ paraplanner, onBack }) => {
  const queryClient = useQueryClient()
  const assignmentsQuery = useQuery(["fsp-admin-assignments"], api.assignments.index)
  const wmQuery = useQuery(["fsp-admin-wealth-managers", ""], () => api.wealthManagers.index())
  const [addOpen, setAddOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const wmById = useMemo(() => {
    const rows: Person[] = wmQuery.data?.data ?? []
    return rows.reduce((acc, row) => ({ ...acc, [row.id]: row.attributes.fullName }), {} as Record<string, string>)
  }, [wmQuery.data])

  const invalidate = () => queryClient.invalidateQueries(["fsp-admin-assignments"])
  const destroyMutation = useMutation((id: number) => api.assignments.destroy(id), { onSuccess: invalidate })
  const updateMutation = useMutation(
    ({ id, attrs }: { id: number; attrs: AssignmentAttrs }) => api.assignments.update(id, attrs),
    // A removal failure is stale the moment the admin does something else, so
    // clear it here rather than leave two error banners stacked up.
    { onMutate: () => destroyMutation.reset(), onSuccess: invalidate }
  )

  const allRows: AssignmentRow[] = assignmentsQuery.data?.data ?? []
  const rows = allRows.filter((r) => r.attributes.paraplannerId === paraplanner.id)

  // Keep a valid selection: default to the first row, and recover if the
  // selected assignment is removed or the list reloads.
  useEffect(() => {
    if (rows.length === 0) {
      if (selectedId !== null) setSelectedId(null)
      return
    }
    if (!selectedId || !rows.some((r) => r.id === selectedId)) {
      setSelectedId(rows[0].id)
    }
  }, [rows, selectedId])

  if (assignmentsQuery.isLoading || wmQuery.isLoading) return <p>Loading…</p>

  const assignedWmIds = rows.map((r) => r.attributes.wealthManagerId)
  const wmName = (id: number) => wmById[String(id)] ?? `Wealth manager #${id}`
  const selected = rows.find((r) => r.id === selectedId) ?? null

  return (
    <div className="fsp-admin__pp-show">
      <Button textLink className="fsp-admin__back" onClick={onBack}>← Back to paraplanners</Button>

      <div className="fsp-admin__pp-header">
        <h1>{paraplanner.fullName}</h1>
        <div className="fsp-admin__pp-email">{paraplanner.email}</div>
      </div>

      <div className="fsp-admin__pp-section-head">
        <h2 className="fsp-admin__pp-section">Wealth managers this paraplanner can work with</h2>
        <Button primary onClick={() => setAddOpen(true)}>
          Assign wealth manager
        </Button>
      </div>

      {/* Surface save/remove failures — both mutations otherwise fail silently.
          One banner per mutation: a shared one mislabels whichever failed
          second. */}
      {updateMutation.isError && (
        <div className="fsp-admin__notice fsp-admin__notice--error" role="alert">
          {failureText(updateMutation.error, "Couldn't save changes")}
        </div>
      )}
      {destroyMutation.isError && (
        <div className="fsp-admin__notice fsp-admin__notice--error" role="alert">
          {failureText(destroyMutation.error, "Couldn't remove the wealth manager")}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="fsp-admin__pp-empty">
          This paraplanner is not linked to any wealth managers yet. Use “Assign wealth manager” to get started.
        </p>
      ) : (
        <div className="fsp-admin__pp-grid">
          {/* Left: list of linked wealth managers */}
          <div className="fsp-admin__pp-wm-list wb-clients__card">
            <table className="wb-clients__table">
              <thead>
                <tr>
                  <th>Wealth manager</th>
                  <th>Access</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const active = row.id === selectedId
                  return (
                    <tr
                      key={row.id}
                      className={`fsp-admin__pp-wm-row ${active ? "fsp-admin__pp-wm-row--active" : ""}`}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <td>
                        <span className="wb-clients__name">{wmName(row.attributes.wealthManagerId)}</span>
                      </td>
                      <td className="fsp-admin__pp-access-cell">
                        {accessSummary(row.attributes.accessStrategy, row.attributes.clientIds.length)}
                      </td>
                      <td className="fsp-admin__pp-wm-actions fsp-admin__row-actions">
                        <button
                          type="button"
                          className="fsp-admin__row-link fsp-admin__row-link--danger"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            setSelectedId(row.id)
                            setRemoveOpen(true)
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {/* Flat footer strip so the last row's bottom edge sits flush with
                  the card's rounded bottom corner. */}
              <tfoot>
                <tr className="fsp-admin__pp-wm-foot">
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Right: detail card for the selected wealth manager */}
          <div className="fsp-admin__pp-detail">
            {selected ? (
              <AssignmentCard
                key={selected.id}
                row={selected}
                wmName={wmName(selected.attributes.wealthManagerId)}
                saving={updateMutation.isLoading}
                onSave={(strategy, clientIds) =>
                  updateMutation.mutate({
                    id: Number(selected.id),
                    attrs: { access_strategy: strategy, client_ids: clientIds },
                  })
                }
              />
            ) : (
              <p className="fsp-admin__pp-empty">Select a wealth manager to manage access.</p>
            )}
          </div>
        </div>
      )}

      {/* Mounted only while open: closing it discards the wealth manager, the
          access choice, the chosen clients and any failure from the last
          attempt, so the next "Assign wealth manager" starts blank. */}
      {addOpen && (
        <AddAssignmentModal
          paraplannerId={paraplanner.id}
          assignedWmIds={assignedWmIds}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            invalidate()
            setAddOpen(false)
          }}
        />
      )}

      <ConfirmDialog
        open={removeOpen && !!selected}
        title="Remove wealth manager"
        danger
        confirmLabel="Remove"
        busy={destroyMutation.isLoading}
        message={
          selected ? (
            <p style={{ margin: 0 }}>
              Remove <strong>{wmName(selected.attributes.wealthManagerId)}</strong> from{" "}
              <strong>{paraplanner.fullName}</strong>? This paraplanner will lose access to that wealth manager’s
              clients.
            </p>
          ) : null
        }
        onConfirm={() => {
          if (!selected) return
          // Mirror of the update mutation's onMutate: a save failure is stale
          // once removal starts, so drop it rather than stack two banners.
          updateMutation.reset()
          destroyMutation.mutate(Number(selected.id), { onSuccess: () => setRemoveOpen(false) })
        }}
        onClose={() => setRemoveOpen(false)}
      />
    </div>
  )
}

// ---- Detail card for one assignment (the selected wealth manager) ----

type CardProps = {
  row: AssignmentRow
  wmName: string
  saving: boolean
  onSave: (strategy: Strategy, clientIds: number[]) => void
}

const AssignmentCard: React.FC<CardProps> = ({ row, wmName, saving, onSave }) => {
  const wmId = row.attributes.wealthManagerId
  const [strategy, setStrategy] = useState<Strategy>(row.attributes.accessStrategy)
  const [clients, setClients] = useState<ClientOption[]>([])

  // Resolve the stored client_ids into names for display when this is a subset
  // assignment. Bounded fetch (only the assignment's own clients).
  const namesQuery = useQuery(
    ["assignment-client-names", row.id, row.attributes.clientIds.join(",")],
    () => api.clients.index({ ids: row.attributes.clientIds, wealth_manager_id: wmId }),
    { enabled: isSubset(row.attributes.accessStrategy) && row.attributes.clientIds.length > 0 }
  )

  useEffect(() => {
    const data = namesQuery.data?.data as { id: string; attributes: { fullName: string } }[] | undefined
    if (data) setClients(data.map((c) => ({ id: Number(c.id), name: c.attributes.fullName })))
  }, [namesQuery.data])

  const changeStrategy = (next: Strategy) => {
    setStrategy(next)
    // Non-subset choices carry no client list, so save straight away.
    if (!isSubset(next)) {
      setClients([])
      onSave(next, [])
    }
  }

  // Which clients does the chosen list refer to? Wording flips for deny_some.
  const pickerLabel = strategy === "deny_some" ? "Clients to hide" : "Clients they can see"

  // `clients` is only trustworthy once the stored ids have been resolved into
  // names. Until then it is [], which looks exactly like "the admin removed
  // everything" — so saving through either of these states would quietly wipe
  // the assignment's client list.
  const namesPending =
    isSubset(row.attributes.accessStrategy) && row.attributes.clientIds.length > 0 && namesQuery.isFetching
  const namesFailed =
    isSubset(row.attributes.accessStrategy) && row.attributes.clientIds.length > 0 && namesQuery.isError

  // Dirty = something to save. Compare the current strategy + chosen client set
  // against what's stored on the assignment.
  const savedIds = [...row.attributes.clientIds].sort((a, b) => a - b)
  const currentIds = clients.map((c) => c.id).sort((a, b) => a - b)
  const idsChanged = savedIds.length !== currentIds.length || savedIds.some((v, i) => v !== currentIds[i])
  const dirty = strategy !== row.attributes.accessStrategy || idsChanged

  return (
    <div className="fsp-admin__pp-card">
      <div className="fsp-admin__pp-card-head">
        <span className="fsp-admin__pp-wm">{wmName}</span>
      </div>

      <fieldset className="fsp-admin__access">
        <legend>What can they see?</legend>
        {ACCESS_CHOICES.map((choice) => (
          <label
            key={choice.value}
            className={`fsp-admin__access-option ${strategy === choice.value ? "fsp-admin__access-option--active" : ""}`}
          >
            <input
              type="radio"
              name={`access-${row.id}`}
              checked={strategy === choice.value}
              onChange={() => changeStrategy(choice.value)}
            />
            <span>{choice.label}</span>
          </label>
        ))}
      </fieldset>

      {isSubset(strategy) && (
        <div className="fsp-admin__pp-clients">
          <div className="fsp-admin__pp-clients-head">
            <label className="fsp-admin__field-label">{pickerLabel}</label>
            <span className="fsp-admin__pp-clients-count">{clients.length} selected</span>
          </div>
          <p className="fsp-admin__hint">Search by name to add clients. Selected clients appear below.</p>
          {namesFailed && (
            <div className="fsp-admin__notice fsp-admin__notice--error" role="alert">
              Couldn't load this assignment's current clients, so saving is held back to avoid clearing them.
              <Button textLink onClick={() => namesQuery.refetch()}>Try again</Button>
            </div>
          )}
          <ClientMultiSelect
            queryKey={`assignment-client-search-${row.id}`}
            fetcher={clientFetcher(wmId)}
            selected={clients}
            onChange={setClients}
          />
          <div className="fsp-admin__pp-clients-actions">
            <Button
              primary
              disabled={saving || namesPending || namesFailed || !dirty}
              // A greyed-out button with no reason reads as broken, and "nothing
              // to save" is the state it sits in most of the time.
              title={saveHint(saving, namesPending, namesFailed, dirty)}
              onClick={() => onSave(strategy, clients.map((c) => c.id))}
            >
              {saving ? "Saving…" : "Save clients"}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Add-a-wealth-manager modal ----

type ModalProps = {
  paraplannerId: number
  assignedWmIds: number[]
  onClose: () => void
  onCreated: () => void
}

const AddAssignmentModal: React.FC<ModalProps> = ({ paraplannerId, assignedWmIds, onClose, onCreated }) => {
  const [wm, setWm] = useState<{ id: number; name: string } | null>(null)
  const [strategy, setStrategy] = useState<Strategy>("allow_all")
  const [clients, setClients] = useState<ClientOption[]>([])

  const createMutation = useMutation((attrs: AssignmentAttrs) => api.assignments.create(attrs), {
    onSuccess: onCreated,
  })

  const submit = () => {
    if (!wm) return
    createMutation.mutate({
      wealth_manager_id: wm.id,
      paraplanner_id: paraplannerId,
      access_strategy: strategy,
      client_ids: isSubset(strategy) ? clients.map((c) => c.id) : [],
    })
  }

  return (
    <Modal open title="Assign a wealth manager" onClose={onClose}>
      {/* Numbered steps share one vertical rhythm (.fsp-admin__form-section) so
          the gaps between label, control and the next step stay even. */}
      <div className="fsp-admin__form-section">
        <label className="fsp-admin__field-label" htmlFor="fsp-assignment-wm">
          1. Choose a wealth manager
        </label>
        <SearchableSelect
          inputId="fsp-assignment-wm"
          queryKey="fsp-admin-add-assignment-wm"
          fetcher={(q) => api.wealthManagers.index(q)}
          value={wm?.id ?? null}
          selectedName={wm?.name ?? null}
          onChange={(id, name) => {
            setWm(id ? { id, name: name ?? "" } : null)
            setClients([])
          }}
          placeholder="Type a name to search"
          excludeIds={assignedWmIds}
        />
      </div>

      <div className="fsp-admin__form-section">
        <fieldset className="fsp-admin__access">
          {/* A <label> can only point at one control, so a radio group takes a
              <legend> — same as the detail card's fieldset above. */}
          <legend>2. Choose what they can see</legend>
          {ACCESS_CHOICES.map((choice) => (
            <label
              key={choice.value}
              className={`fsp-admin__access-option ${strategy === choice.value ? "fsp-admin__access-option--active" : ""}`}
            >
              <input
                type="radio"
                name="access-new"
                checked={strategy === choice.value}
                onChange={() => setStrategy(choice.value)}
              />
              <span>{choice.label}</span>
            </label>
          ))}
        </fieldset>
      </div>

      {isSubset(strategy) && (
        <div className="fsp-admin__form-section">
          <label className="fsp-admin__field-label">3. Choose the clients</label>
          {wm ? (
            <ClientMultiSelect
              queryKey="fsp-admin-add-assignment-clients"
              fetcher={clientFetcher(wm.id)}
              selected={clients}
              onChange={setClients}
            />
          ) : (
            <p className="fsp-admin__hint">Choose a wealth manager first.</p>
          )}
        </div>
      )}

      {createMutation.isError && (
        <div className="fsp-admin__notice fsp-admin__notice--error" role="alert">
          Couldn't add the wealth manager
          {(createMutation.error as { message?: string } | null)?.message
            ? `: ${(createMutation.error as { message?: string }).message}`
            : ""}
          . Please try again.
        </div>
      )}

      <div className="fsp-admin__modal-actions">
        <Button secondary type="button" onClick={onClose}>Cancel</Button>
        <Button primary disabled={!wm || createMutation.isLoading} onClick={submit}>
          {createMutation.isLoading ? "Adding…" : "Add wealth manager"}
        </Button>
      </div>
    </Modal>
  )
}
